<script setup>
// Stripe設定
// https://dashboard.stripe.com/
// テスト用カード
// https://pay.jp/docs/testcard
// 参考
// https://arrown-blog.com/php-laravel-stripe-creditcard2/

import { inject, ref, reactive, watch, computed, onMounted } from 'vue';
import axios from 'axios';
import swal from 'sweetalert';
import Alert from '@util/BrowserAlert';
import ImageFrame from '@component/ImageFrame.vue';
import { loadStripe } from '@stripe/stripe-js';

/**
 * 支払方法登録モーダル（Stripe Elements）
 *
 * - 既存会員のカード登録（既定）: POST /payment/setup-intent → stripe.confirmCardSetup()（必要なら 3DS 認証）
 *     → POST /payment {setup_intent} → 成功時は /payment/end へ
 *     保存時に 3DS を済ませるので、以後の月次（off_session）決済で 3DS を求められない
 * - 新規会員登録（有料プラン）  : storeUrl に /questioner/register/payment を渡す
 *     サーバーの JSON 応答
 *       {payment_confirm_url}  … 3DS 認証へ遷移
 *       {redirect_url}         … 本登録完了などで画面遷移
 *       {errors, retry}        … エラー表示。retry=true ならモーダルを開いたままやり直し
 */
const props = defineProps({
    storeUrl: { type: String, default: null },
    plan_id: { type: Number, default: null },
    payment_intent_client_secret: { type: String, default: null },
});

const emit = defineEmits(['completed', 'canceled', 'failed']);

const publishableKey = inject('stripePublicKey', '');

// injectで取得した値がRef型の場合、.valueでアクセス
const stripePublicKey = (publishableKey && typeof publishableKey === 'object' && 'value' in publishableKey)
    ? publishableKey.value
    : publishableKey;

let stripe = null;
let cardNumberElement = null;
let cardCvcElement = null;
let cardExpiryElement = null;
const cardHolder = ref('');
const paymentEnd = ref('');
const showLoading = ref(false)

const errors = reactive({
    cardNumber: {
        message: '',
        completed: false,
    },
    cardCvc: {
        message: '',
        completed: false,
    },
    cardExpiry: {
        message: '',
        completed: false,
    },
});
let processing = false;
const close = ref();

const onCompleted = computed(() => {
    return errors.cardNumber.completed && errors.cardCvc.completed && errors.cardExpiry.completed;
});

const onClickCancel = () => {
    if (processing) return;
    emit('canceled');
};

// 文字数チェック
watch(
    () => cardHolder.value,
    (value) => {
        if(Alert.charLimitText(value)){
            cardHolder.value = value.substring(0 ,Alert.TEXT_LIMIT_NUMBER);
        }
    }
);

onMounted(async () => {
    // Stripeを初期化
    if (!stripePublicKey || typeof stripePublicKey !== 'string') {
        console.error('stripePublicKey is empty or invalid');
        Alert.Error('Stripeの公開鍵が設定されていません。');
        return;
    }

    try {
        stripe = await loadStripe(stripePublicKey);

        if (!stripe) {
            console.error('Failed to load Stripe');
            Alert.Error('Stripeの初期化に失敗しました。');
            return;
        }
    } catch (error) {
        console.error('Error loading Stripe:', error);
        Alert.Error('Stripeの読み込み中にエラーが発生しました。');
        return;
    }

    var elements = stripe.elements();

    // Element作成時に options から スタイルを調整できます.
    var style = {
        base: {
            fontSize: '24px',
            color: '#000',
            '::placeholder': {
                color: '#cdd6dd',
            },
        },
        complete: {
            color: '#000',
        },
        empty: {
            color: '#000',
        },
        invalid: {
            color: '#000',
        }
    };

    // card Element のインスタンス作成
    cardNumberElement = elements.create('cardNumber', { style: style, showIcon: true, placeholder: 'カード番号' });
    cardCvcElement = elements.create('cardCvc', { style: style, placeholder: '' });
    cardExpiryElement = elements.create('cardExpiry', { style: style });

    // マウント
    cardNumberElement.mount('#card-number');
    cardCvcElement.mount('#card-cvc');
    cardExpiryElement.mount('#card-expiry');

    // 入力フォームのチェック
    cardNumberElement.addEventListener('change', (event) => {
        errors.cardNumber.completed = event.complete;
        errors.cardNumber.message = event?.error?'カード番号が正しくありません。' : null;
    });
    cardCvcElement.addEventListener('change', (event) => {
        errors.cardCvc.completed = event.complete;
        errors.cardCvc.message = event?.error?.message ?? null;
    });
    cardExpiryElement.addEventListener('change', (event) => {
        errors.cardExpiry.completed = event.complete;
        errors.cardExpiry.message = event?.error?'有効期限が過ぎています。' : null;
    });

    var form = document.getElementById('payment-form');
    form?.addEventListener('submit', async (event) => {
        // デフォルトのSubmit処理を停止させる
        event.preventDefault();

        if (processing) return;

        if (!props.storeUrl) {
            // 既存会員のカード登録・変更 → SetupIntent で保存（3DS 認証込み）
            saveCardWithSetupIntent();
            return;
        }

        // 新規会員登録（有料）: 従来どおり Token。3DS は初月決済のオーソリで行う
        stripe.createToken(cardNumberElement, { name: cardHolder.value }).then((result) => {
            if (result.error) {
                showError('決済を確認できませんでした。\nお手数ですが、もう一度支払方法登録からやり直してください。');
            } else {
                stripeTokenHolder(result.token);
            }
        });
    });
});

// 本人認証（3DS）が完了しなかったときのエラーコード
const SETUP_AUTH_ERROR_CODES = ['setup_intent_authentication_failure', 'authentication_required'];

/**
 * 既存会員のカード登録・変更（SetupIntent）
 * 1. サーバで SetupIntent を作成（client_secret を受け取る）
 * 2. stripe.confirmCardSetup() でカードを保存。発行会社が求めれば 3DS 認証画面がモーダルで表示される
 * 3. 保存できたら SetupIntent の ID をサーバへ送り、既定のカードとして登録
 */
const saveCardWithSetupIntent = async () => {
    processing = true;
    showLoading.value = true;

    let clientSecret = null;
    try {
        const { data } = await axios.post(route('payment.setup_intent'));
        if (data?.errors) {
            showError(data.errors, () => { window.location.reload(); });
            return;
        }
        clientSecret = data?.client_secret;
    } catch (error) {
        console.error('setup intent failed:', error);
        showError('カードの登録を開始できませんでした。\nお手数ですが、画面を再読み込みしてもう一度お試しください。', () => { window.location.reload(); });
        return;
    }

    const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
            card: cardNumberElement,
            billing_details: { name: cardHolder.value },
        },
    });

    if (result.error) {
        const code = result.error.code ?? '';
        if (SETUP_AUTH_ERROR_CODES.includes(code)) {
            showError('本人確認(3Dセキュア)に失敗しました。\nもう一度お試しいただくか、別のカードをご登録ください。');
        } else {
            showError('カードが登録できませんでした。入力内容が正しいかご確認下さい。正しく入力しても登録できない場合、別のカードでご登録下さい。');
        }
        return;
    }

    processing = false; // postToStore 側で再度 true にする
    postToStore({ setup_intent: result.setupIntent.id });
};

/**
 * エラーを表示してモーダルは開いたまま再入力できる状態に戻す
 */
const showError = (message, afterClose = null) => {
    showLoading.value = false;
    processing = false;

    return swal({
        title: "確認",
        text: message,
        icon: "warning",
        buttons: {
            ok: {
                text: "OK",
                className: "timeout-ok-btn",
            },
        },
    }).then(() => {
        if (afterClose) {
            afterClose();
        } else {
            emit('failed', message);
        }
    });
};

const stripeTokenHolder = (token) => {
    // カード情報をトークン化してサーバーに送る
    postToStore({ token: token.id, card_token: token.card.id });
};

const postToStore = (params) => {
    if (processing) return;

    processing = true;
    showLoading.value = true;

    const url = props.storeUrl ?? route('payment.store');

    axios.post(url, params)
        .then((response) => {
            const data = response?.data ?? {};

            if (data.errors) {
                if (data.redirect_url) {
                    // セッション切れ・登録済みなど。メッセージを見せてから遷移
                    showError(data.errors, () => { window.location.href = data.redirect_url; });
                } else if (data.retry === false) {
                    // やり直し不可（想定外エラー・登録処理失敗）。画面を再読込して状態を取り直す
                    showError(data.errors, () => { window.location.reload(); });
                } else {
                    // カード登録失敗・決済拒否など。モーダルを開いたままやり直し
                    showError(data.errors);
                }
                return;
            }

            if (data.payment_confirm_url) {
                // 3DS2 認証が必要
                window.location.href = data.payment_confirm_url;
                return;
            }

            if (data.redirect_url) {
                // 本登録完了など
                window.location.href = data.redirect_url;
                return;
            }

            // 既存会員のカード登録完了
            emit('completed', data.payment);
            paymentEnd.value.click();
        })
        .catch((error) => {
            // 419（セッション切れ）・500・通信断など。モーダル内で再送しても直らないので OK で再読込して最初から
            console.error('payment store failed:', error);
            showError('決済を確認できませんでした。\nお手数ですが、もう一度支払方法登録からやり直してください。', () => { window.location.reload(); });
        });
}
</script>
<template>
    <!--modal START-->
    <form id="payment-form" method="post">
        <div id="payment-modal" class="modal type3 js-modal">
            <div class="modal-bg bt_modal_close" @click="onClickCancel"></div>
            <div class="modal-content">
                <div class="close_bt bt_modal_close" @click="onClickCancel">
                    <image-frame src="images/common/i_bt_close.svg" />
                </div>

                <div class="pop" v-show="!showLoading">
                    <h2>支払方法登録</h2>
                    <div class="form-row sec_2 last" style="margin-bottom: 50px">
                        <h3>お支払い方法を入力ください</h3>

                        <div class="inner">
                            <p class="sub_t" style="font-size: 18px">カード番号</p>
                            <!-- Stripe処理でElementが追加される -->
                            <div id="card-number" class="w60" style="height: 62px; border: solid 1px #ccc; border-radius: 4px; line-height: 1; padding: 12px;"> </div>
                            <span>※カード番号は16桁。スペース 、ハイフン不要</span>
                            <div v-if="errors.cardNumber.message"><span class="err">{{ errors.cardNumber.message }}</span></div>
                            <br />

                            <p class="sub_t" style="font-size: 18px">名義人</p>
                            <input id="card-name" v-model="cardHolder" class="w60" type="text" placeholder="名義人" />
                            <br />

                            <!-- Stripe処理でElementが追加される -->
                            <p class="sub_t" style="font-size: 18px">セキュリティーコード</p>
                            <div id="card-cvc" class="w30" style="height: 62px; border: solid 1px #ccc; border-radius: 4px; line-height: 1; padding: 12px;"> </div>
                            <span>※〈MasterCard、VISA、JCB、AMEX〉<br>
                                カード裏面の署名欄に印字された末尾3桁の数字です。
                            </span>
                            <div v-if="errors.cardCvc.message"><span class="err">{{ errors.cardCvc.message }}</span></div>
                            <br />

                            <!-- Stripe処理でElementが追加される -->
                            <p class="sub_t" style="font-size: 18px">有効期限</p>
                            <div id="card-expiry" class="w20" style="border: solid 1px #ccc; border-radius: 4px; line-height: 1; padding: 12px;"> </div>
                            <div v-if="errors.cardExpiry.message"><span class="err">{{ errors.cardExpiry.message }}</span></div>
                            <span style="color: red; font-size: 19px">
                                <br>カード会社が提供する 本人認証画面 が表示される場合があります。<br>
                                本人認証画面 が表示された場合には本人認証を完了してください。
                            </span>
                        </div>
                    </div>
                    <div class="bt_box clear">
                        <ul class="modalBtn-area b2">
                            <li>
                                <button type="button" class="bt_clear bt_modal_close" @click="onClickCancel">キャンセル</button>
                            </li>
                            <li>
                                <button class="bt_ok" :disabled="!onCompleted">OK</button>
                            </li>
                        </ul>
                        <p class="bt_modal_close" ref="close" style="display:none"></p>
                    </div>
                </div>
                <div class="pop" v-show="showLoading">
                    <div class="spinner-container">
                        <div class="spinner">
                            <ImageFrame src="images/index/loading.gif" ></ImageFrame>
                        </div>

                        <div class="message-box">
                            <p><strong>現在決済処理中です</strong></p>
                            <p>ボタンを押したり移動したりせず、このまましばらくお待ちください。処理が完了したら自動的に画面が変わります。</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <a :href="route('payment.end')" ref="paymentEnd" style="display: none"></a>
    </form>
    <!--modal END-->
</template>
<style scoped>
  .spinner-container {
    text-align: center;
  }

  .spinner {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto 20px;
  }

  .message-box {
    border: 2px solid red;
    padding: 10px;
    color: red;
    max-width: 300px;
    margin: 0 auto;
  }

  .message-box p {
    margin: 5px 0;
  }

  .message-box strong {
    font-weight: bold;
  }
</style>
